/**
 * Admin -> Edge Fleet -> Agent updates (release 3.2 patch management, phase 2).
 *
 * Rolls a FOCTTA-signed agent version out to the endpoint fleet:
 *
 *   - a PILOT first (named devices, a wave or a business unit), then
 *     percentage WAVES (10% / 50% / 100% by default) of the devices that can
 *     take the version, each soaking (24 h for the pilot, 12 h after) before
 *     the next starts;
 *   - an automatic HALT when more than 2% of a stage's devices report failed
 *     or rolled back, and a hold while nothing has reported;
 *   - FOUR EYES: an operator prepares and submits a rollout; a different person
 *     with the approver permission starts it. The console never offers the
 *     creator the Approve button, and the controller and the database refuse
 *     it anyway;
 *   - HOLD ALL: one switch that stops every rollout and every new update
 *     instruction (devices mid-update finish, or roll back by themselves);
 *   - ROLL BACK a rollout, and WITHDRAW a version, each showing the exact
 *     publisher command to run offline: the controller signs nothing.
 *     REINSTATING a withdrawn version is two approvers (one proposes, another
 *     confirms), and devices keep refusing it until a signed withdrawn list
 *     without it is published;
 *   - an AUTO-HALT raises a banner here (and a notification, see
 *     update-rollout-sweep.ts).
 *
 * What each device does with an instruction is docs/edge-endpoint/SELF-UPDATE.md.
 * Buttons follow the permissions (useCan); the controller decides.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Copy, Loader2, OctagonPause, PackageCheck, Play, RefreshCw, RotateCcw, ShieldOff } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listBusinessUnits, listEdgeInstallTokens, problemDetail } from '@/lib/api';
import { useCan, useMe } from '@/lib/access';
import {
  approveRollout,
  cancelRollout,
  confirmReinstate,
  createRollout,
  engageUpdateHold,
  fourEyesBlocks,
  getRollout,
  getUpdatesOverview,
  haltedRollouts,
  isOwnProposal,
  isPlainVersion,
  listRolloutDevices,
  MEMBER_STATUS_LABEL,
  parseWaves,
  pauseRollout,
  proposeReinstate,
  releaseUpdateHold,
  resumeRollout,
  rollbackRollout,
  ROLLOUT_STATE_LABEL,
  submitRollout,
  UPDATE_APPROVE,
  UPDATE_WRITE,
  withdrawVersion,
  type CreateRolloutInput,
  type Rollout,
  type RolloutDetail,
  type RolloutDevice,
  type RolloutMemberStatus,
  type RolloutState,
  type UpdatesOverview,
  type WithdrawnVersion,
} from '@/lib/update-rollouts-api';

/** Release 8: a new rollout's update window, laptops' afternoon and evening (device local time). */
export const DEFAULT_ROLLOUT_WINDOW = '15:30-20:00';

/** A rollout the URL names (`?rollout=<id>`, the halt notification's link). */
function rolloutFromUrl(): string | null {
  try {
    const id = new URLSearchParams(window.location.search).get('rollout');
    return id && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** How a signed withdrawn list reaches the devices (PR #139's publish step), said once. */
const PUBLISH_STEPS =
  "Run these on the release signer's machine: the first signs the list into a folder of yours, the second puts it " +
  'on the agent download folder only while that folder still serves the list it was built on (never copy the ' +
  'files by hand). For an https download host, run the second with --check-only immediately before uploading ' +
  'withdrawn.json.sig and then withdrawn.json.';

const DEVICES_KEEP_REFUSING = (v: string) =>
  `Devices keep refusing ${v} until a new signed withdrawn list without it is published. ${PUBLISH_STEPS}`;

/** One withdrawn version: its reason, and reinstating it (two approvers). */
function WithdrawnRow({
  w,
  canApprove,
  actorId,
  onChanged,
  onReinstated,
}: {
  w: WithdrawnVersion;
  canApprove: boolean;
  actorId: string | null;
  onChanged: (notice: string) => void;
  onReinstated: (version: string, commands: string[]) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const own = isOwnProposal(w.reinstateProposal, actorId);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(problemDetail(e, 'could not reinstate the version'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="space-y-1" data-testid={`withdrawn-${w.version}`}>
      <div>
        <span className="font-mono text-foreground">{w.version}</span>{' '}
        <span className="text-muted-foreground">- {w.reason} ({w.withdrawnBy}, {when(w.at)})</span>
      </div>
      {w.reinstateProposal ? (
        <div className="text-muted-foreground">
          Reinstating proposed by {w.reinstateProposal.by} ({when(w.reinstateProposal.at)}): {w.reinstateProposal.reason}
        </div>
      ) : null}
      {canApprove && !w.reinstateProposal ? (
        <div className="flex flex-wrap items-end gap-2">
          <ReasonInput id={`reinstate-reason-${w.version}`} value={reason} onChange={setReason} label="Why reinstate it (a different approver confirms)" />
          <Button size="sm" variant="glass" disabled={busy || reason.trim().length < 3} data-testid={`propose-reinstate-${w.version}`}
            onClick={() => void run(async () => {
              await proposeReinstate(w.version, reason.trim());
              setReason('');
              onChanged(`Reinstating ${w.version} proposed; a different approver must confirm it.`);
            })}>
            Propose reinstating
          </Button>
        </div>
      ) : null}
      {canApprove && w.reinstateProposal ? (
        <div className="space-y-1">
          <Button size="sm" variant="glass" disabled={busy || own} title={own ? 'You proposed it; a different approver confirms' : undefined}
            data-testid={`confirm-reinstate-${w.version}`}
            onClick={() => void run(async () => {
              const x = await confirmReinstate(w.version);
              onReinstated(x.version, x.publisherCommands);
            })}>
            Confirm reinstating
          </Button>
          {own ? <p className="text-muted-foreground" data-testid="reinstate-four-eyes-note">Four eyes: you proposed it; a different approver must confirm it.</p> : null}
        </div>
      ) : null}
      {error ? <p className="text-destructive" role="alert">{error}</p> : null}
    </li>
  );
}

const INPUT_CLASS = 'rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm text-foreground';
const ACTIVE: readonly RolloutState[] = ['awaiting_approval', 'running', 'paused', 'halted'];

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : 'never';
}

export function stateVariant(s: RolloutState): 'success' | 'warning' | 'critical' | 'glass' {
  if (s === 'running' || s === 'completed') return 'success';
  if (s === 'halted' || s === 'rolled_back') return 'critical';
  if (s === 'paused' || s === 'awaiting_approval') return 'warning';
  return 'glass';
}

/** A command the operator copies and runs offline. */
function CommandBlock({ commands, label }: { commands: string[]; label: string }) {
  const [copied, setCopied] = useState(false);
  if (commands.length === 0) return null;
  const text = commands.join('\n');
  return (
    <div className="space-y-1" data-testid="publisher-commands">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => undefined);
          }}
          aria-label="Copy the commands"
        >
          <Copy className="mr-1 h-3 w-3" /> {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded border border-border/40 bg-muted/30 p-2 font-mono text-[11px] text-foreground">
        {text}
      </pre>
    </div>
  );
}

function ReasonInput({ id, value, onChange, label }: { id: string; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <div className="min-w-48 flex-1">
      <label htmlFor={id} className="mb-1 block text-xs text-muted-foreground">
        {label}
      </label>
      <input id={id} className={`w-full ${INPUT_CLASS}`} value={value} maxLength={500} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const COUNT_COLUMNS: Array<[RolloutMemberStatus, string]> = [
  ['instructed', 'Told'],
  ['updated', 'Updated'],
  ['failed', 'Failed'],
  ['rolled_back', 'Rolled back'],
  ['managed_externally', 'Own tool'],
  ['stood_down', 'Stood down'],
  ['rollback_requested', 'Rollback asked'],
  ['reverted', 'Reverted'],
];

// ── the rollout in detail ──────────────────────────────────────────────────

function RolloutCard({
  detail,
  actorId,
  canWrite,
  canApprove,
  held,
  onChanged,
}: {
  detail: RolloutDetail;
  actorId: string | null;
  canWrite: boolean;
  canApprove: boolean;
  held: boolean;
  onChanged: (notice: string) => void;
}) {
  const r = detail.rollout;
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rollbackOpen, setRollbackOpen] = useState(false);
  const [commands, setCommands] = useState<string[]>([]);
  const [devices, setDevices] = useState<RolloutDevice[] | null>(null);
  const [deviceFilter, setDeviceFilter] = useState<RolloutMemberStatus | ''>('');
  const blocked = fourEyesBlocks(r, actorId);

  useEffect(() => {
    let live = true;
    listRolloutDevices(r.id, { ...(deviceFilter ? { status: deviceFilter } : {}), limit: 100 })
      .then((x) => live && setDevices(x.devices))
      .catch(() => live && setDevices([]));
    return () => {
      live = false;
    };
  }, [r.id, r.updatedAt, deviceFilter]);

  const run = async (what: string, fn: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      const notice = await fn();
      setReason('');
      onChanged(notice);
    } catch (e) {
      setError(problemDetail(e, `${what} failed`));
    } finally {
      setBusy(false);
    }
  };
  const reasonOk = reason.trim().length >= 3;

  return (
    <div className="space-y-3 rounded-md border border-border/40 bg-muted/10 p-3" data-testid="rollout-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm font-semibold text-foreground">{r.targetVersion}</span>
        <Badge variant={stateVariant(r.state)} className="text-[10px]" data-testid="rollout-state">
          {ROLLOUT_STATE_LABEL[r.state]}
        </Badge>
        {r.currentStageLabel ? <span className="text-xs text-muted-foreground">at {r.currentStageLabel}</span> : null}
        {held && r.state === 'running' ? <Badge variant="critical" className="text-[10px]">On hold</Badge> : null}
      </div>
      <p className="text-xs text-muted-foreground">
        {r.platforms.join(' and ')} {r.arch} - pilot{' '}
        {r.pilot.type === 'devices' ? `${r.pilot.podIds.length} named device(s)` : r.pilot.type === 'install_token' ? 'one wave' : 'one business unit'}
        , then {r.waves.map((w) => `${w}%`).join(', ')}; soak {r.pilotSoakHours} h for the pilot, {r.waveSoakHours} h after; a stage passes
        once {r.minReportedPercent}% of its devices have reported; window {r.updateWindow ?? "each device's own (the one its installer set, else 20:00-08:00)"}.
      </p>
      <p className="text-xs text-foreground">Why: {r.reason}</p>
      <p className="text-[11px] text-muted-foreground">
        Created by {r.createdBy} {when(r.createdAt)}
        {r.submittedBy ? `; submitted by ${r.submittedBy}` : ''}
        {r.approvedBy ? `; approved and started by ${r.approvedBy} ${when(r.approvedAt)}` : ''}
      </p>
      {r.stateReason ? (
        <p className={`text-xs ${r.state === 'halted' ? 'text-red-700 dark:text-red-300' : 'text-foreground'}`} data-testid="rollout-state-reason">
          {r.stateReason}
        </p>
      ) : null}
      {detail.gate && detail.gate.action === 'hold' ? (
        <p className="text-xs text-muted-foreground" data-testid="rollout-gate">Next: {detail.gate.reason}</p>
      ) : null}

      {detail.preview ? (
        <div className="rounded border border-border/30 p-2 text-xs text-foreground" data-testid="rollout-preview">
          <div>
            {detail.preview.eligible} of {detail.preview.endpointAgents} endpoint agents can take {r.targetVersion}; pilot:{' '}
            {detail.preview.pilot.eligible} to update{detail.preview.pilot.atTarget ? `, ${detail.preview.pilot.atTarget} already on it` : ''}.
          </div>
          <div className="text-muted-foreground">
            Then {detail.preview.waves.map((w) => `${w.label}: about ${w.eligible}`).join(', ')}.
            {detail.preview.tooOld ? ` ${detail.preview.tooOld} agent(s) are older than 0.0.16 and cannot update themselves: use your installer or MDM.` : ''}
            {detail.preview.pilot.notFound.length ? ` Not found: ${detail.preview.pilot.notFound.join(', ')}.` : ''}
          </div>
        </div>
      ) : null}

      {r.state !== 'draft' && r.state !== 'awaiting_approval' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular" data-testid="rollout-stages">
            <thead>
              <tr className="border-b border-border/30 text-left text-muted-foreground">
                <th className="py-1 pr-3 font-normal">Stage</th>
                {COUNT_COLUMNS.map(([k, label]) => (
                  <th key={k} className="py-1 pr-3 font-normal">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.stages.map((s) => (
                <tr key={s.stage} className={`border-b border-border/15 ${r.currentStage === s.stage ? 'font-medium' : ''}`}>
                  <td className="py-1 pr-3 whitespace-nowrap">{s.label} - soak {s.soakHours} h</td>
                  {COUNT_COLUMNS.map(([k]) => (
                    <td key={k} className="py-1 pr-3 tabular-nums">
                      {/* Failed: what the gate counts, a failure kept through a stand-down or a resume. */}
                      {k === 'failed' ? (s.gate?.failed ?? s.counts.failed) : (s.counts[k] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}

      <div className="flex flex-wrap items-end gap-2">
        {(r.state === 'running' || r.state === 'paused' || r.state === 'halted' || r.state === 'draft' || r.state === 'awaiting_approval') &&
        (canWrite || canApprove) ? (
          <ReasonInput id={`reason-${r.id}`} value={reason} onChange={setReason} label="Reason (recorded in the audit log)" />
        ) : null}
        {r.state === 'draft' && canWrite ? (
          <Button size="sm" disabled={busy} onClick={() => void run('Submit', async () => {
            await submitRollout(r.id);
            return 'Submitted for approval.';
          })}>
            Submit for approval
          </Button>
        ) : null}
        {r.state === 'awaiting_approval' && canApprove ? (
          <Button
            size="sm"
            disabled={busy || blocked !== null || held}
            title={blocked ?? (held ? 'Agent updates are on hold' : 'Starts the pilot now')}
            data-testid="approve-rollout"
            onClick={() => {
              // Review L3: approving STARTS the pilot; say so before it happens.
              const pilot = detail.preview ? `${detail.preview.pilot.eligible} pilot device(s)` : 'the pilot';
              if (!window.confirm(`Approve and start the rollout of ${r.targetVersion}? ${pilot} will be told to update now, then the waves follow by themselves (${r.waves.map((w) => `${w}%`).join(', ')}).`)) return;
              void run('Approve', async () => {
              const x = await approveRollout(r.id);
              return `Approved and started: ${x.started.instructed} pilot device(s) told to update.`;
              });
            }}
          >
            <PackageCheck className="mr-1 h-3 w-3" /> Approve and start
          </Button>
        ) : null}
        {r.state === 'running' && canWrite ? (
          <Button size="sm" variant="glass" disabled={busy || !reasonOk} onClick={() => void run('Pause', async () => {
            const x = await pauseRollout(r.id, reason.trim());
            return `Paused: ${x.standDown.stoodDown} device(s) stood down, ${x.standDown.inFlight} mid-update left to finish.`;
          })}>
            <OctagonPause className="mr-1 h-3 w-3" /> Pause
          </Button>
        ) : null}
        {r.state === 'paused' && canApprove ? (
          <Button size="sm" disabled={busy || !reasonOk || held} onClick={() => void run('Resume', async () => {
            const x = await resumeRollout(r.id, reason.trim());
            return `Resumed: ${x.reinstructed} device(s) told again; the soak starts again.`;
          })}>
            <Play className="mr-1 h-3 w-3" /> Resume
          </Button>
        ) : null}
        {(r.state === 'running' || r.state === 'paused' || r.state === 'halted' || r.state === 'completed') && canApprove ? (
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => setRollbackOpen((o) => !o)} data-testid="open-rollback">
            <RotateCcw className="mr-1 h-3 w-3" /> Roll back...
          </Button>
        ) : null}
        {ACTIVE.includes(r.state) || r.state === 'draft' ? (
          canWrite ? (
            <Button size="sm" variant="ghost" disabled={busy || !reasonOk} onClick={() => void run('Cancel', async () => {
              const x = await cancelRollout(r.id, reason.trim());
              return `Cancelled${x.standDown.stoodDown ? `: ${x.standDown.stoodDown} device(s) stood down` : ''}.`;
            })}>
              Cancel rollout
            </Button>
          ) : null
        ) : null}
      </div>
      {blocked && r.state === 'awaiting_approval' && canApprove ? (
        <p className="text-xs text-muted-foreground" data-testid="four-eyes-note">Four eyes: {blocked}</p>
      ) : null}

      {rollbackOpen ? (
        <div className="space-y-2 rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="rollback-panel">
          <p className="text-xs text-foreground">
            Asks the {detail.rollbackPlan.devices} device(s) that updated in this rollout to go back to the version they ran before
            ({detail.rollbackPlan.fromVersions.join(', ') || 'none yet'}); devices that have not started are stood down. A device goes back
            only with a SIGNED rollback manifest naming its version: run these on the release signer's machine and publish the
            .rollback.json(.sig) files into the agent download folder first (devices retry until then).
          </p>
          <CommandBlock commands={commands.length ? commands : detail.rollbackPlan.commands} label="Publisher commands (offline, release key)" />
          <div className="flex flex-wrap items-end gap-2">
            <ReasonInput id={`rollback-reason-${r.id}`} value={reason} onChange={setReason} label="Reason for the rollback (recorded in the audit log)" />
            <Button size="sm" variant="destructive" disabled={busy || !reasonOk} data-testid="confirm-rollback" onClick={() => void run('Roll back', async () => {
              const x = await rollbackRollout(r.id, reason.trim());
              setCommands(x.publisherCommands);
              return `Rolled back: ${x.rollbackRequested} device(s) asked for their previous version, ${x.standDown.stoodDown} stood down.`;
            })}>
              <RotateCcw className="mr-1 h-3 w-3" /> Roll back this rollout
            </Button>
          </div>
        </div>
      ) : null}

      {devices !== null && (r.state !== 'draft' && r.state !== 'awaiting_approval') ? (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Devices in this rollout</span>
            <select aria-label="Device status" className="glass-lite rounded border border-border/40 bg-background/40 px-2 py-1 text-xs" value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value as RolloutMemberStatus | '')}>
              <option value="">All</option>
              {(Object.keys(MEMBER_STATUS_LABEL) as RolloutMemberStatus[]).map((k) => (
                <option key={k} value={k}>{MEMBER_STATUS_LABEL[k]}</option>
              ))}
            </select>
          </div>
          {devices.length === 0 ? (
            <p className="text-xs text-muted-foreground">None.</p>
          ) : (
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-xs" data-testid="rollout-devices">
                <thead>
                  <tr className="border-b border-border/30 text-left text-muted-foreground">
                    <th className="py-1 pr-3 font-normal">Device</th>
                    <th className="py-1 pr-3 font-normal">Stage</th>
                    <th className="py-1 pr-3 font-normal">From</th>
                    <th className="py-1 pr-3 font-normal">Running</th>
                    <th className="py-1 pr-3 font-normal">Result</th>
                    <th className="py-1 pr-3 font-normal">Last reported</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((dv) => (
                    <tr key={dv.podId} className="border-b border-border/15">
                      <td className="py-1 pr-3">{dv.hostname ?? dv.displayName ?? dv.podId}</td>
                      <td className="py-1 pr-3 whitespace-nowrap">{dv.stageLabel}</td>
                      <td className="py-1 pr-3 font-mono">{dv.fromVersion}</td>
                      <td className="py-1 pr-3 font-mono">{dv.version ?? '-'}</td>
                      <td className="py-1 pr-3">{MEMBER_STATUS_LABEL[dv.status] ?? dv.status}</td>
                      <td className="py-1 pr-3 text-muted-foreground">
                        {dv.lastState ? `${dv.lastState}${dv.lastReason ? ` (${dv.lastReason})` : ''}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── a new rollout ──────────────────────────────────────────────────────────

function NewRolloutForm({ onCreated }: { onCreated: (r: Rollout, notice: string) => void }) {
  const [targetVersion, setTargetVersion] = useState('');
  const [windows, setWindows] = useState(true);
  const [linux, setLinux] = useState(false);
  const [pilotType, setPilotType] = useState<'devices' | 'install_token' | 'business_unit'>('devices');
  const [podIds, setPodIds] = useState('');
  const [scopeId, setScopeId] = useState('');
  const [waves, setWaves] = useState('10, 50, 100');
  const [pilotSoak, setPilotSoak] = useState('24');
  const [waveSoak, setWaveSoak] = useState('12');
  const [minReported, setMinReported] = useState('50');
  // Release 8 (founder decision, 17 Oct 2026): laptops are switched on in the
  // afternoon and evening, not at night, so a Windows-only rollout (the laptop
  // agent) proposes 15:30-20:00 local time. A window sent with a rollout
  // REPLACES each device's own (the installer's or the BigFix task's
  // UpdateWindow) and the device keeps it until a later rollout sends another,
  // so the laptop window is proposed only for Windows alone: ticking Linux
  // (servers) empties it, unless the operator typed their own (review).
  const [windowText, setWindowText] = useState(DEFAULT_ROLLOUT_WINDOW);
  const [windowEdited, setWindowEdited] = useState(false);
  useEffect(() => {
    if (windowEdited) return;
    setWindowText(windows && !linux ? DEFAULT_ROLLOUT_WINDOW : '');
  }, [windows, linux, windowEdited]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<Array<{ id: string }>>([]);
  const [units, setUnits] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    listEdgeInstallTokens().then((x) => setTokens(x.tokens.map((t) => ({ id: t.id })))).catch(() => undefined);
    listBusinessUnits().then((x) => setUnits(x.businessUnits.map((b) => ({ id: b.id, name: b.name })))).catch(() => undefined);
  }, []);

  const parsedWaves = parseWaves(waves);
  const pilotIds = podIds.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const problems: string[] = [];
  if (!isPlainVersion(targetVersion)) problems.push('a version like 0.0.17');
  if (!windows && !linux) problems.push('at least one platform');
  if ('problem' in parsedWaves) problems.push(parsedWaves.problem);
  if (pilotType === 'devices' && pilotIds.length === 0) problems.push('at least one pilot device');
  if (pilotType !== 'devices' && !scopeId) problems.push(pilotType === 'install_token' ? 'a pilot wave' : 'a pilot business unit');
  if (windowText.trim() && !/^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/.test(windowText.trim())) problems.push('a window like 20:00-08:00');
  if (reason.trim().length < 3) problems.push('a reason');

  const submit = async (andSubmit: boolean) => {
    if (problems.length > 0 || 'problem' in parsedWaves) return;
    setBusy(true);
    setError(null);
    const input: CreateRolloutInput = {
      targetVersion: targetVersion.trim(),
      platforms: [...(windows ? (['windows'] as const) : []), ...(linux ? (['linux'] as const) : [])],
      pilot:
        pilotType === 'devices'
          ? { type: 'devices', podIds: pilotIds }
          : pilotType === 'install_token'
            ? { type: 'install_token', installTokenId: scopeId }
            : { type: 'business_unit', businessUnitId: scopeId },
      waves: parsedWaves.waves,
      pilotSoakHours: Number(pilotSoak),
      waveSoakHours: Number(waveSoak),
      minReportedPercent: Number(minReported),
      updateWindow: windowText.trim() || null,
      reason: reason.trim(),
      submit: andSubmit,
    };
    try {
      const { rollout } = await createRollout(input);
      onCreated(rollout, andSubmit ? 'Rollout submitted: a different person with the approver permission starts it.' : 'Draft saved.');
      setReason('');
    } catch (e) {
      setError(problemDetail(e, 'could not create the rollout'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="new-rollout-heading" className="space-y-2 rounded-md border border-border/40 p-3" data-testid="new-rollout">
      <h3 id="new-rollout-heading" className="text-sm font-semibold text-foreground">New rollout</h3>
      <p className="text-xs text-muted-foreground">
        Publish and sign the version first (SELF-UPDATE.md section 10); the console only tells devices which version to fetch.
      </p>
      <div className="grid gap-2 md:grid-cols-3">
        <label className="text-xs text-muted-foreground">
          Version
          <input aria-label="Target version" className={`mt-1 block w-full font-mono ${INPUT_CLASS}`} value={targetVersion} placeholder="0.0.17" maxLength={32} onChange={(e) => setTargetVersion(e.target.value)} />
        </label>
        <fieldset className="text-xs text-muted-foreground">
          <legend>Platforms (x64)</legend>
          <label className="mr-3 inline-flex items-center gap-1 text-foreground">
            <input type="checkbox" checked={windows} onChange={(e) => setWindows(e.target.checked)} /> Windows
          </label>
          <label className="inline-flex items-center gap-1 text-foreground">
            <input type="checkbox" checked={linux} onChange={(e) => setLinux(e.target.checked)} /> Linux
          </label>
        </fieldset>
        <label className="text-xs text-muted-foreground">
          Maintenance window (device local time)
          <input
            aria-label="Update window"
            className={`mt-1 block w-full font-mono ${INPUT_CLASS}`}
            value={windowText}
            maxLength={11}
            onChange={(e) => {
              setWindowEdited(true);
              setWindowText(e.target.value);
            }}
          />
          <span className="block mt-1" data-testid="update-window-hint">
            15:30-20:00 suits laptops (on in the afternoon and evening); 20:00-08:00 suits servers. A window here replaces
            each device&apos;s own (the one its installer or BigFix task set) and the device keeps it until a later rollout
            sends another. Empty sends none: each device keeps the window it has now.
          </span>
        </label>
        <label className="text-xs text-muted-foreground">
          Pilot
          <select aria-label="Pilot type" className={`mt-1 block w-full ${INPUT_CLASS}`} value={pilotType} onChange={(e) => { setPilotType(e.target.value as typeof pilotType); setScopeId(''); }}>
            <option value="devices">Named devices</option>
            <option value="install_token">One wave (install token)</option>
            <option value="business_unit">One business unit</option>
          </select>
        </label>
        {pilotType === 'devices' ? (
          <label className="text-xs text-muted-foreground md:col-span-2">
            Pilot device ids (instance ids from the device list, comma or space separated)
            <textarea aria-label="Pilot devices" className={`mt-1 block w-full font-mono ${INPUT_CLASS}`} rows={2} value={podIds} onChange={(e) => setPodIds(e.target.value)} />
          </label>
        ) : (
          <label className="text-xs text-muted-foreground md:col-span-2">
            {pilotType === 'install_token' ? 'Pilot wave' : 'Pilot business unit'}
            <select aria-label="Pilot scope" className={`mt-1 block w-full ${INPUT_CLASS}`} value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">Choose...</option>
              {pilotType === 'install_token'
                ? tokens.map((t) => <option key={t.id} value={t.id}>Wave {t.id.slice(0, 8)}</option>)
                : units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-muted-foreground">
          Waves after the pilot (% of the fleet)
          <input aria-label="Waves" className={`mt-1 block w-full font-mono ${INPUT_CLASS}`} value={waves} onChange={(e) => setWaves(e.target.value)} />
        </label>
        <label className="text-xs text-muted-foreground">
          Soak, pilot / later waves (hours)
          <span className="mt-1 flex gap-1">
            <input aria-label="Pilot soak hours" className={`w-16 ${INPUT_CLASS}`} value={pilotSoak} inputMode="numeric" onChange={(e) => setPilotSoak(e.target.value)} />
            <input aria-label="Wave soak hours" className={`w-16 ${INPUT_CLASS}`} value={waveSoak} inputMode="numeric" onChange={(e) => setWaveSoak(e.target.value)} />
          </span>
        </label>
        <label className="text-xs text-muted-foreground">
          A stage passes once this % has reported
          <input aria-label="Minimum reported percent" className={`mt-1 block w-20 ${INPUT_CLASS}`} value={minReported} inputMode="numeric" onChange={(e) => setMinReported(e.target.value)} />
        </label>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <ReasonInput id="new-rollout-reason" value={reason} onChange={setReason} label="Why this rollout (recorded in the audit log)" />
        <Button size="sm" variant="glass" disabled={busy || problems.length > 0} onClick={() => void submit(false)}>Save draft</Button>
        <Button size="sm" disabled={busy || problems.length > 0} onClick={() => void submit(true)} data-testid="submit-new-rollout">Submit for approval</Button>
      </div>
      {problems.length > 0 ? <p className="text-[11px] text-muted-foreground">Still needed: {problems.join(', ')}.</p> : null}
      {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
    </section>
  );
}

// ── the panel ──────────────────────────────────────────────────────────────


export function UpdateRolloutsPanel() {
  const canWrite = useCan(UPDATE_WRITE);
  const canApprove = useCan(UPDATE_APPROVE);
  const { me } = useMe();
  const actorId = me?.actorId ?? null;
  const [overview, setOverview] = useState<UpdatesOverview | null>(null);
  const [selected, setSelected] = useState<string | null>(() => rolloutFromUrl());
  const [reinstatedNow, setReinstatedNow] = useState<{ version: string; commands: string[] } | null>(null);
  const [detail, setDetail] = useState<RolloutDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [holdReason, setHoldReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [withdrawText, setWithdrawText] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawCommand, setWithdrawCommand] = useState<{ commands: string[]; altered: boolean } | null>(null);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const o = await getUpdatesOverview();
      setOverview(o);
      setError(null);
      const pick = selected ?? o.rollouts.find((r) => ACTIVE.includes(r.state))?.id ?? o.rollouts[0]?.id ?? null;
      if (pick) {
        setSelected(pick);
        setDetail(await getRollout(pick));
      } else {
        setDetail(null);
      }
    } catch (e) {
      setError(problemDetail(e, 'could not read agent updates'));
    }
  }, [selected]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  const changed = (n: string) => {
    setNotice(n);
    void load();
  };

  const hold = overview?.hold;

  const engage = async () => {
    if (!window.confirm('Hold EVERY agent update for this tenant? No rollout moves or starts and no device is told to update until an approver releases the hold. Devices already updating finish, or roll back by themselves.')) return;
    setBusy(true);
    try {
      const x = await engageUpdateHold(holdReason.trim());
      setHoldReason('');
      changed(`Every agent update is on hold: ${x.standDown.stoodDown} device(s) stood down, ${x.standDown.inFlight} mid-update left to finish or roll back by itself.`);
    } catch (e) {
      setError(problemDetail(e, 'could not hold updates'));
    } finally {
      setBusy(false);
    }
  };
  const release = async () => {
    setBusy(true);
    try {
      const x = await releaseUpdateHold(holdReason.trim());
      setHoldReason('');
      changed(`Hold released: ${x.reinstructed} device(s) told again; running rollouts soak again from now.`);
    } catch (e) {
      setError(problemDetail(e, 'could not release the hold'));
    } finally {
      setBusy(false);
    }
  };
  const withdraw = async () => {
    const v = withdrawText.trim();
    if (!window.confirm(`Withdraw ${v}? No rollout or single-device update can target it, and a rollout targeting it halts. Lifting it later takes two approvers.`)) return;
    setBusy(true);
    try {
      const x = await withdrawVersion(v, withdrawReason.trim());
      setWithdrawCommand({ commands: x.publisherCommands, altered: x.reasonInCommand !== x.reason });
      setWithdrawText('');
      setWithdrawReason('');
      changed(
        `${x.version} withdrawn in the console${x.halted.length ? `; ${x.halted.length} rollout(s) halted` : ''}${x.cancelled.length ? `, ${x.cancelled.length} cancelled` : ''}. Agents refuse it only once the signed list is published (command below).`,
      );
    } catch (e) {
      setError(problemDetail(e, 'could not withdraw the version'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassCard className="mt-4 p-0" data-testid="update-rollouts-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <PackageCheck className="h-4 w-4" />
            Agent updates
          </CardTitle>
          <CardDescription>
            Roll a signed agent version out to your endpoint agents: a pilot first, then waves, each soaking before the next. A stage
            where more than 2% of the devices fail or roll back halts the rollout by itself. One person prepares a rollout, a different
            person approves and starts it. Hold stops every update at once; devices already updating finish or roll back by themselves.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload agent updates">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>

      <div className="space-y-5 px-6 pb-6">
        {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : null}
        {notice ? <p className="text-xs text-foreground" data-testid="updates-notice">{notice}</p> : null}
        {overview === null && !error ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>
        ) : null}

        {overview && haltedRollouts(overview.rollouts).length > 0 ? (
          <div className="space-y-1 rounded-md border border-red-500/40 bg-red-500/10 p-3" role="alert" data-testid="halt-banner">
            {haltedRollouts(overview.rollouts).map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  Rollout of {r.targetVersion} HALTED{r.haltedAt ? ` at ${when(r.haltedAt)}` : ''}: {r.stateReason ?? 'halted'}. Roll it back or cancel it.
                </p>
                <Button size="sm" variant="glass" onClick={() => {
                  setSelected(r.id);
                  void getRollout(r.id).then(setDetail).catch(() => undefined);
                }}>
                  Show it
                </Button>
              </div>
            ))}
          </div>
        ) : null}

        {hold ? (
          <section aria-labelledby="update-hold-heading" className="space-y-2">
            <h3 id="update-hold-heading" className="text-sm font-semibold text-foreground">Hold all updates</h3>
            {hold.held ? (
              <div className="space-y-2 rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="hold-banner">
                <p className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
                  <AlertTriangle className="h-4 w-4" /> Every agent update is ON HOLD since {when(hold.at)} by {hold.setBy}: {hold.reason}
                </p>
                <p className="text-xs text-muted-foreground">No rollout moves or starts and no device is told to update until an approver releases the hold.</p>
                {canApprove ? (
                  <div className="flex flex-wrap items-end gap-2">
                    <ReasonInput id="hold-release-reason" value={holdReason} onChange={setHoldReason} label="Reason for releasing (recorded in the audit log)" />
                    <Button size="sm" disabled={busy || holdReason.trim().length < 3} onClick={() => void release()} data-testid="release-hold">
                      <Play className="mr-1 h-3 w-3" /> Release the hold
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : canWrite ? (
              <div className="flex flex-wrap items-end gap-2 rounded-md border border-border/40 bg-muted/20 p-3">
                <ReasonInput id="hold-reason" value={holdReason} onChange={setHoldReason} label="Reason (recorded in the audit log)" />
                <Button size="sm" variant="destructive" disabled={busy || holdReason.trim().length < 3} onClick={() => void engage()} data-testid="engage-hold">
                  <ShieldOff className="mr-1 h-3 w-3" /> Hold every update
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Agent updates are not on hold.</p>
            )}
          </section>
        ) : null}

        {overview && !overview.fleetWideCaller ? (
          <p className="text-sm text-muted-foreground">Rollouts span business units; they are managed by operators with access to every unit.</p>
        ) : null}

        {overview && overview.fleetWideCaller ? (
          <section aria-labelledby="rollouts-heading" className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h3 id="rollouts-heading" className="text-sm font-semibold text-foreground">Rollouts</h3>
              {canWrite ? (
                <Button size="sm" variant="glass" onClick={() => setShowNew((v) => !v)}>
                  {showNew ? 'Close' : 'New rollout'}
                </Button>
              ) : null}
            </div>
            {showNew && canWrite ? (
              <NewRolloutForm
                onCreated={(r, n) => {
                  setShowNew(false);
                  setSelected(r.id);
                  changed(n);
                }}
              />
            ) : null}
            {overview.rollouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No rollout yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="rollouts-table">
                  <thead>
                    <tr className="border-b border-border/30 text-left text-muted-foreground">
                      <th className="py-1 pr-3 font-normal">Version</th>
                      <th className="py-1 pr-3 font-normal">Platforms</th>
                      <th className="py-1 pr-3 font-normal">State</th>
                      <th className="py-1 pr-3 font-normal">Stage</th>
                      <th className="py-1 pr-3 font-normal">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.rollouts.map((r) => (
                      <tr
                        key={r.id}
                        className={`cursor-pointer border-b border-border/15 hover:bg-muted/20 ${selected === r.id ? 'bg-muted/20' : ''}`}
                        onClick={() => {
                          setSelected(r.id);
                          void getRollout(r.id).then(setDetail).catch(() => undefined);
                        }}
                      >
                        <td className="py-1 pr-3 font-mono">{r.targetVersion}</td>
                        <td className="py-1 pr-3">{r.platforms.join(', ')}</td>
                        <td className="py-1 pr-3"><Badge variant={stateVariant(r.state)} className="text-[10px]">{ROLLOUT_STATE_LABEL[r.state]}</Badge></td>
                        <td className="py-1 pr-3">{r.currentStageLabel ?? '-'}</td>
                        <td className="py-1 pr-3 text-muted-foreground">{when(r.createdAt)} by {r.createdBy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {detail ? (
              <RolloutCard
                key={detail.rollout.id}
                detail={detail}
                actorId={actorId}
                canWrite={canWrite}
                canApprove={canApprove}
                held={hold?.held === true}
                onChanged={changed}
              />
            ) : null}
          </section>
        ) : null}

        {overview ? (
          <section aria-labelledby="withdrawn-heading" className="space-y-2">
            <h3 id="withdrawn-heading" className="text-sm font-semibold text-foreground">Withdrawn versions</h3>
            {overview.withdrawn.length === 0 ? (
              <p className="text-sm text-muted-foreground">No version is withdrawn.</p>
            ) : (
              <ul className="space-y-2 text-xs" data-testid="withdrawn-list">
                {overview.withdrawn.map((w) => (
                  <WithdrawnRow
                    key={w.version}
                    w={w}
                    canApprove={canApprove}
                    actorId={actorId}
                    onChanged={changed}
                    onReinstated={(version, commands) => {
                      setReinstatedNow({ version, commands });
                      changed(`${version} reinstated in the console. Devices keep refusing it until the signed list is published.`);
                    }}
                  />
                ))}
              </ul>
            )}
            {canApprove ? (
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-xs text-muted-foreground">
                  Version
                  <input aria-label="Version to withdraw" className={`mt-1 block w-28 font-mono ${INPUT_CLASS}`} value={withdrawText} maxLength={32} onChange={(e) => setWithdrawText(e.target.value)} />
                </label>
                <ReasonInput id="withdraw-reason" value={withdrawReason} onChange={setWithdrawReason} label="Why (recorded, and in the signed list)" />
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={busy || !isPlainVersion(withdrawText) || withdrawReason.trim().length < 3}
                  onClick={() => void withdraw()}
                  data-testid="withdraw-version"
                >
                  Withdraw
                </Button>
              </div>
            ) : null}
            {reinstatedNow ? (
              <div className="space-y-1" data-testid="reinstated-now">
                <p className="text-xs text-foreground">{DEVICES_KEEP_REFUSING(reinstatedNow.version)}</p>
                <CommandBlock commands={reinstatedNow.commands} label="Publisher commands (offline, release key)" />
              </div>
            ) : null}
            {overview.reinstated.length > 0 ? (
              <div className="space-y-2" data-testid="reinstated-list">
                <h4 className="text-xs font-semibold text-foreground">Reinstated in the console (last 30 days)</h4>
                {overview.reinstated.map((x) => (
                  <div key={`${x.version}-${x.at}`} className="space-y-1 text-xs">
                    <p className="text-foreground">
                      <span className="font-mono">{x.version}</span> - proposed by {x.proposedBy}, confirmed by {x.reinstatedBy} ({when(x.at)}): {x.reason}.{' '}
                      {DEVICES_KEEP_REFUSING(x.version)}
                    </p>
                    <CommandBlock commands={x.publisherCommands} label="Publisher commands (offline, release key)" />
                  </div>
                ))}
              </div>
            ) : null}
            {withdrawCommand ? (
              <div className="space-y-1">
                <p className="text-xs text-foreground">
                  The console can no longer target it. For the AGENTS to refuse it, sign and publish the withdrawn list offline with the
                  release key. {PUBLISH_STEPS}
                </p>
                {withdrawCommand.altered ? (
                  <p className="text-xs text-muted-foreground" data-testid="reason-simplified">
                    The reason in the command is simplified to plain characters without quotes, so it pastes the same in bash and
                    PowerShell; the full reason is in the console and the audit log.
                  </p>
                ) : null}
                <CommandBlock commands={withdrawCommand.commands} label="Publisher commands (offline, release key)" />
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    </GlassCard>
  );
}
